/**
 * The enforcement toggle's lock state, as ONE function.
 *
 * WHY THIS IS A MODULE AND NOT AN INLINE TERNARY
 *
 * `is_enforcement_active` (backend/services/rbac.py) consults the `ROLE_ENFORCEMENT`
 * environment override BEFORE it loads the config row, so while that variable is set the
 * Settings → Identity toggle has no effect at all: pressing it writes the DB flag, the
 * override still wins, and enforcement does not move.
 *
 * The page used to explain that in a two-sentence paragraph. FINISH-LINE item 18 removed
 * the paragraph and replaced it with a disabled control carrying a four-word label — which
 * only stays honest if the disabled state and the label are IMPOSSIBLE TO SEPARATE. A
 * disabled control with no label is the same defect in quieter form: the operator sees a
 * control that will not move and is told nothing about why.
 *
 * So the pairing is not two independent JSX expressions that a future edit can desynchronise.
 * It is one value: a lock either exists — carrying both its `disabled` flag and a non-empty
 * `label` — or it does not exist and the toggle is live. There is no representable state in
 * which the toggle is locked and unlabelled.
 *
 * This file has NO IMPORTS and NO JSX deliberately, so `tests/test_identity_enforcement_lock.py`
 * can transpile it with the esbuild already in node_modules and EXECUTE it, rather than
 * inspecting the shape of a guard clause and hoping.
 */

/**
 * What the deploy environment says, as reported by `GET /api/identity/rbac-config`.
 *
 * `true` = ROLE_ENFORCEMENT=on, `false` = ROLE_ENFORCEMENT=off, `null`/`undefined` = not
 * set, so the DB toggle decides. `undefined` is accepted because the config response is
 * optional-shaped in the client while it loads.
 */
export type EnforcementOverride = boolean | null | undefined

/** A locked toggle. Both fields or neither — see the module header. */
export interface EnforcementLock {
  /** Always `true`. The toggle must be rendered disabled while a lock exists. */
  readonly disabled: true
  /** Short reason, rendered beside the toggle. Never empty. */
  readonly label: string
}

/**
 * The lock for a given override, or `null` when the toggle is genuinely operable.
 *
 * Both override directions lock the control. Forced-OFF is not the friendlier case: a
 * toggle reading "Enable enforcement" that cannot enable anything is exactly as
 * misdescribing as one that cannot disable.
 */
export function enforcementLock(override: EnforcementOverride): EnforcementLock | null {
  if (override === true) return { disabled: true, label: 'Forced on by deploy setting' }
  if (override === false) return { disabled: true, label: 'Forced off by deploy setting' }
  return null
}
